/* newbuild-esp.jsx — ESP catalog and integration options for New Build.
   Ported from _handoff/esp-catalog.jsx (VD-02 direction).
   Defines window.EspStep and window.ESP_CATALOG_NAMES. Selection is local UI
   state; newbuild-model.js gates the structured build payload. */

(function () {
  const { useState, useRef, useEffect } = React;

  // Registry-shaped catalog. tag → 'New'; info → tooltip string.
  const CATALOG = [
    { n: 'Mailchimp' }, { n: 'Campaign Monitor' }, { n: 'Marketo (V2.0)' },
    { n: 'Marketo (V3.0)', info: 'V3.0 uses the newer Marketo email syntax — confirm your instance is migrated.' },
    { n: 'Braze' }, { n: 'Pardot' },
    { n: 'Pardot Lightning', tag: 'New' }, { n: 'HubSpot — HUBL' }, { n: 'Klaviyo' },
    { n: 'HubSpot (COS)' }, { n: 'Deployteq' }, { n: 'IBM Watson Campaign' },
    { n: 'iContact' }, { n: 'PostUp' }, { n: 'Oracle Eloqua' },
    { n: 'Exact Target (Content Builder)' }, { n: 'Salesforce Lightning', tag: 'New' }, { n: 'Dotdigital' },
    { n: 'SendGrid' }, { n: 'Constant Contact' }, { n: 'emfluence' },
    { n: 'ActiveCampaign' }, { n: 'VerticalResponse' }, { n: 'Oracle Responsys' },
    { n: 'ClickDimensions' }, { n: 'Bronto' }, { n: 'Epsilon Harmony' },
    { n: 'Emarsys' }, { n: 'Cheetahmail' }, { n: 'SharpSpring' },
    { n: 'Salesforce Marketing Cloud (SFMC)' }, { n: 'Adobe Campaign' }, { n: 'Acoustic Campaign' },
    { n: 'MyEmma' }, { n: 'GetResponse' }, { n: 'Adobe Campaign Classic' },
    { n: 'Iterable' }, { n: 'Mautic' }, { n: 'Mailkit' },
    { n: 'MailCampaigns' }, { n: 'Web Power' }, { n: 'Zeta' },
    { n: 'Sign-Up.to' }, { n: 'TaxiForEmail' }, { n: 'Cordial' },
    { n: 'Adestra' }, { n: 'InfusionSoft' }, { n: 'Drip' },
    { n: 'MailBlaze' }, { n: 'MoonMail' }, { n: 'Ometria' },
    { n: 'Benchmark' }, { n: 'Copernica' }, { n: 'Pinpointe' },
    { n: 'Customer.io', info: 'Transactional + broadcast. We build for the broadcast (Newsletter) editor unless told otherwise.' },
    { n: 'Any other', info: 'Not listed? We map it to the closest supported syntax and confirm with you.' },
  ];
  const byName = (n) => CATALOG.find(e => e.n === n);

  function Info({ text }) {
    return (
      <span className="esp2-info" tabIndex={0}>i<span className="esp2-tip">{text}</span></span>
    );
  }

  function EspStep({ esp, onEsp, onEspDetail }) {
    const [selected, setSelected] = useState([]);           // [name]
    const [tiers, setTiers] = useState({});                 // name -> 'basic'|'advanced'
    const [notes, setNotes] = useState('');
    const [q, setQ] = useState('');
    const [expanded, setExpanded] = useState(false);        // catalog grid open?
    const ref = useRef(null);

    // keep the parent's single-string esp in sync for the preflight + payload
    useEffect(() => {
      if (!onEsp) return;
      if (!selected.length) onEsp('No ESP (placeholders)');
      else if (selected.length === 1) onEsp(selected[0]);
      else onEsp(selected[0] + ' +' + (selected.length - 1) + ' more');
    }, [selected]);

    // publish the STRUCTURED selection so the preflight can list each ESP + its
    // esp_tier, and the payload can carry them (PY-02 Decision 3: never drop).
    useEffect(() => {
      if (!onEspDetail) return;
      onEspDetail({
        list: selected.map(n => ({ esp: n, esp_tier: tiers[n] || 'basic' })),
        notes: notes,
      });
    }, [selected, tiers, notes]);

    // Port note: the handoff dispatched setTiers from inside the setSelected updater (a side effect
    // React may re-run) and read a stale `tiers` closure on remove. Same semantics, two pure updaters:
    // a name is in `tiers` exactly when it is selected.
    const toggle = (name) => {
      setSelected(s => (s.includes(name) ? s.filter(x => x !== name) : [...s, name]));
      setTiers(t => {
        if (name in t) { const next = { ...t }; delete next[name]; return next; }
        return { ...t, [name]: 'basic' };
      });
    };
    const setTier = (name, v) => setTiers(t => ({ ...t, [name]: v }));

    const list = CATALOG.filter(e => e.n.toLowerCase().includes(q.toLowerCase()));
    const shown = expanded || q ? list : list.slice(0, 9);

    return (
      <div className="esp2" ref={ref} data-esp-step data-esp-selected={selected.length}>
        {/* default / summary line */}
        <div className="esp2-head">
          <div className={'esp2-default' + (selected.length ? ' has' : '')} data-esp-summary>
            {selected.length
              ? <React.Fragment><I.check size={13} /> {selected.length} ESP{selected.length > 1 ? 's' : ''} selected</React.Fragment>
              : <React.Fragment><span className="esp2-dot"></span> No ESP — ships with placeholder links (default)</React.Fragment>}
          </div>
          <span className="muted esp2-count">{CATALOG.length} supported</span>
        </div>

        {/* catalog */}
        <div className="esp2-search">
          <I.search size={15} />
          <input aria-label="Search email service providers" placeholder="Search email service providers…" value={q} onChange={e => setQ(e.target.value)} />
          {q && <button className="esp2-clear" onClick={() => setQ('')}><I.x size={13} /></button>}
        </div>
        <div className="esp2-grid">
          {shown.map(e => {
            const on = selected.includes(e.n);
            return (
              <button type="button" key={e.n} className={'esp2-tile' + (on ? ' sel' : '')} data-esp-option={e.n} aria-pressed={on} onClick={() => toggle(e.n)}>
                <span className="esp2-box">{on && <I.check size={12} />}</span>
                <span className="esp2-name">{e.n}</span>
                {e.tag && <span className="esp2-new">{e.tag}</span>}
                {e.info && <Info text={e.info} />}
              </button>
            );
          })}
          {!list.length && <div className="esp2-empty">No ESP matches “{q}”</div>}
        </div>
        {!q && list.length > 9 && (
          <button type="button" className="esp2-more" data-action="esp-show-all" aria-expanded={expanded} onClick={() => setExpanded(x => !x)}>
            {expanded ? 'Show less' : 'Show all ' + list.length + ' providers'}
            <I.chevron size={13} style={{ transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)' }} />
          </button>
        )}

        {/* integration table */}
        {selected.length > 0 && (
          <div className="esp2-table">
            <div className="esp2-tr esp2-thead">
              <div>Selected provider</div>
              <div>Type of integration</div>
              <div></div>
            </div>
            {selected.map(name => (
              <div className="esp2-tr" key={name} data-esp-row={name}>
                <div className="esp2-prov">{name}</div>
                <div className="esp2-seg-cell">
                  <div className="esp2-seg">
                    <button type="button" data-esp-tier="basic" aria-pressed={tiers[name] === 'basic'} className={tiers[name] === 'basic' ? 'on' : ''} onClick={() => setTier(name, 'basic')}>Basic</button>
                    <button type="button" data-esp-tier="advanced" aria-pressed={tiers[name] === 'advanced'} className={tiers[name] === 'advanced' ? 'on' : ''} onClick={() => setTier(name, 'advanced')}>Advanced</button>
                  </div>
                  <Info text="Basic: templated build in your ESP syntax. Advanced: dynamic content, AMPscript/handlebars logic, personalization blocks." />
                </div>
                <div className="esp2-rm">
                  <button type="button" onClick={() => toggle(name)} title="Remove" aria-label={'Remove ' + name}><I.x size={14} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* special instructions */}
        {selected.length > 0 && (
          <div className="esp2-notes">
            <label className="esp2-notes-lbl">Special instructions <span className="muted">— optional</span></label>
            <textarea aria-label="ESP special instructions" className="esp2-notes-in" rows={2} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Anything specific for these integrations — dynamic blocks, personalization, ESP account quirks, send setup…" />
          </div>
        )}
      </div>
    );
  }

  window.EspStep = EspStep;
  // shared platform-name list so other surfaces (e.g. the variant review's manual
  // ESP add) offer the same catalog without duplicating it.
  window.ESP_CATALOG_NAMES = CATALOG.map(e => e.n);
})();
